import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, shareReplay } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import {
	PayMethodsPagination,
	UpdateEmployeeBankAccountDTO,
	UpdateEmployeeBankAccountRankDTO
} from "../types/accounts";

@Injectable({
	providedIn: "root"
})
export class PayElectiveBankAccountsApiService {
	private employeePayMethodsCache = new Map<string, Observable<PayMethodsPagination>>();

	private http: HttpClient = inject(HttpClient);

	private clearEmployeePayMethodsCache(employeeId?: string): void {
		if (employeeId) {
			this.employeePayMethodsCache.delete(employeeId.trim());
			return;
		}

		this.employeePayMethodsCache.clear();
	}

	createEmployeeBankAccount(bankAccount: any): Observable<any> {
		if (bankAccount?.employeeId) {
			this.clearEmployeePayMethodsCache(bankAccount.employeeId);
		}

		return this.http.post(`${environment.apiUri}/v1/employees/pay-methods/bank`, bankAccount);
	}

	updateEmployeeBankAccount(bankAccount: UpdateEmployeeBankAccountDTO): Observable<any> {
		this.clearEmployeePayMethodsCache();
		return this.http.put(`${environment.apiUri}/v1/employees/pay-methods/bank`, bankAccount);
	}

	updateEmployeeBankAccountRank(bankAccount: UpdateEmployeeBankAccountRankDTO): Observable<any> {
		this.clearEmployeePayMethodsCache();
		return this.http.post(`${environment.apiUri}/v1/employees/pay-methods/reorder`, bankAccount);
	}

	getEmployeeBankAccounts(employeeId: string): Observable<PayMethodsPagination> {
		return this.http.get<PayMethodsPagination>(
			`${environment.apiUri}/v1/employees/${employeeId}/pay-methods/details?payMethodTypes=BANK&pageSize=1000`
		);
	}

	getEmployeeCards(employeeId: string): Observable<PayMethodsPagination> {
		return this.http.get<PayMethodsPagination>(
			`${environment.apiUri}/v1/employees/${employeeId}/pay-methods/details?payMethodTypes=CARD&pageSize=1000`
		);
	}

	getEmployeePaymentMethods(employeeId: string): Observable<PayMethodsPagination> {
		const cacheKey = employeeId.trim();
		const cachedPayMethods = this.employeePayMethodsCache.get(cacheKey);

		if (cachedPayMethods) {
			return cachedPayMethods;
		}

		const payMethods$ = this.http
			.get<PayMethodsPagination>(
				`${environment.apiUri}/v1/employees/${cacheKey}/pay-methods/details?payMethodTypes=CARD,BANK&pageSize=1000`
			)
			.pipe(
				shareReplay({ bufferSize: 1, refCount: false }),
				catchError(error => {
					this.clearEmployeePayMethodsCache(cacheKey);
					return throwError(() => error);
				})
			);

		this.employeePayMethodsCache.set(cacheKey, payMethods$);
		return payMethods$;
	}

	deleteEmployeeBankAccount(employeeId: string, paymethodId: string): Observable<any> {
		this.clearEmployeePayMethodsCache(employeeId);
		return this.http.delete(`${environment.apiUri}/v1/employees/${employeeId}/pay-methods/${paymethodId}`);
	}

	reProcessEmployeeBankAccounts(payMethodIds: string[]) {
		this.clearEmployeePayMethodsCache();
		return this.http.post(`${environment.apiUri}/v1/employees/pay-methods/reprocess`, {
			payMethodIds: [...payMethodIds]
		});
	}

	reProcessAll(payGroupId: string): Observable<any> {
		this.clearEmployeePayMethodsCache();
		return this.http.post(`${environment.apiUri}/v1/employees/pay-methods/${payGroupId}/reprocess`, {});
	}
}
